// 2026-05-25 Admin 站內通知管理頁
// 列表 + 新增/編輯 dialog（四語標題內文、type、分眾 ALL/PLAN/USER、啟用、到期日）
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Box,
  Switch,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { fmtDate } from "utils/datetime";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { COLORS } from "theme/tokens";

const LOCALES = [
  { code: "zh-TW", label: "繁中" },
  { code: "zh-CN", label: "简中" },
  { code: "en-US", label: "EN (US)" },
  { code: "en-GB", label: "EN (UK)" },
];
const TYPES = ["INFO", "WARNING", "PROMO", "MAINTENANCE"];
const PLAN_CODES = ["FREE", "STARTER", "GROWTH", "PRO"];

const TYPE_COLOR = {
  INFO: "#2d80eb",
  WARNING: COLORS.warning,
  PROMO: COLORS.main,
  MAINTENANCE: COLORS.error,
};

const apiCall = (url, method, body, token) =>
  fetch(url, {
    method: method || "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => r.json());

const emptyForm = () => ({
  id: null,
  title: { "zh-TW": "", "zh-CN": "", "en-US": "", "en-GB": "" },
  content: { "zh-TW": "", "zh-CN": "", "en-US": "", "en-GB": "" },
  type: "INFO",
  audienceType: "ALL",
  targetPlans: [],
  targetUserIds: [],
  isActive: true,
  expiresAt: "",
});

export default function AdminNotifications() {
  const { token } = useAuth();
  const [list, setList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());

  // 指定使用者 picker
  const [userKeyword, setUserKeyword] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [pickedUsers, setPickedUsers] = useState([]); // {id,email}

  const fetchList = useCallback(() => {
    apiCall("/api/admin/notifications", "GET", null, token)
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch(() => setList([]));
  }, [token]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openAdd = () => {
    setForm(emptyForm());
    setPickedUsers([]);
    setDialogOpen(true);
  };

  const openEdit = (n) => {
    setForm({
      id: n.id,
      title: { "zh-TW": "", "zh-CN": "", "en-US": "", "en-GB": "", ...(n.title || {}) },
      content: { "zh-TW": "", "zh-CN": "", "en-US": "", "en-GB": "", ...(n.content || {}) },
      type: n.type || "INFO",
      audienceType: n.audienceType || "ALL",
      targetPlans: Array.isArray(n.targetPlans) ? n.targetPlans : [],
      targetUserIds: Array.isArray(n.targetUserIds) ? n.targetUserIds : [],
      isActive: n.isActive !== false,
      expiresAt: n.expiresAt ? String(n.expiresAt).slice(0, 10) : "",
    });
    // 編輯時用 targetUserIds 還原成 chip（只有 id，email 顯示 id 尾段）
    setPickedUsers(
      (Array.isArray(n.targetUserIds) ? n.targetUserIds : []).map((id) => ({ id, email: id }))
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // 繁中為 fallback 來源，必填
    if (!form.title["zh-TW"]?.trim() || !form.content["zh-TW"]?.trim()) {
      window.alert("請至少填寫繁中的標題與內文");
      return;
    }
    if (form.audienceType === "PLAN" && form.targetPlans.length === 0) {
      window.alert("依方案分眾請至少選一個方案");
      return;
    }
    if (form.audienceType === "USER" && pickedUsers.length === 0) {
      window.alert("指定個別使用者請至少選一位");
      return;
    }
    const payload = {
      ...form,
      targetUserIds: pickedUsers.map((u) => u.id),
      expiresAt: form.expiresAt || null,
    };
    const url = form.id ? "/api/admin/notifications/update" : "/api/admin/notifications/create";
    await apiCall(url, "POST", payload, token);
    setDialogOpen(false);
    fetchList();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("確定刪除這則通知？")) return;
    await apiCall("/api/admin/notifications/delete", "POST", { id }, token);
    fetchList();
  };

  const toggleActive = async (n) => {
    await apiCall("/api/admin/notifications/update", "POST", { id: n.id, isActive: !n.isActive }, token);
    fetchList();
  };

  const searchUsers = async (kw) => {
    setUserKeyword(kw);
    if (kw.trim().length < 2) {
      setUserResults([]);
      return;
    }
    const res = await apiCall(
      `/api/admin/notifications/search-users?keyword=${encodeURIComponent(kw)}`,
      "GET",
      null,
      token
    );
    setUserResults(Array.isArray(res) ? res : []);
  };

  const addUser = (u) => {
    if (!pickedUsers.find((p) => p.id === u.id)) setPickedUsers([...pickedUsers, u]);
    setUserKeyword("");
    setUserResults([]);
  };
  const removeUser = (id) => setPickedUsers(pickedUsers.filter((p) => p.id !== id));

  const setTitleLang = (code, v) => setForm((f) => ({ ...f, title: { ...f.title, [code]: v } }));
  const setContentLang = (code, v) => setForm((f) => ({ ...f, content: { ...f.content, [code]: v } }));

  const togglePlan = (code) =>
    setForm((f) => ({
      ...f,
      targetPlans: f.targetPlans.includes(code)
        ? f.targetPlans.filter((p) => p !== code)
        : [...f.targetPlans, code],
    }));

  const audienceLabel = (n) => {
    if (n.audienceType === "ALL") return "全站";
    if (n.audienceType === "PLAN") return `方案：${(n.targetPlans || []).join("/")}`;
    if (n.audienceType === "USER") return `指定 ${(n.targetUserIds || []).length} 人`;
    return n.audienceType;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={3} sx={{ background: COLORS.grayLighter, minHeight: "100vh" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography sx={{ fontSize: "20px", fontWeight: 700, color: COLORS.black }}>
            🔔 站內通知管理
          </MDTypography>
          <MDButton
            onClick={openAdd}
            startIcon={<Icon>add</Icon>}
            sx={{
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            新增通知
          </MDButton>
        </Box>

        <Card sx={{ p: 0, borderRadius: 3, boxShadow: "0 4px 16px rgba(0,0,0,0.04)", overflow: "hidden" }}>
          {/* header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "26% 12% 18% 12% 16% 16%",
              px: 3,
              py: 1.5,
              backgroundColor: COLORS.grayLightest,
              borderBottom: "1px solid #eef0f3",
            }}
          >
            {["標題 (繁中)", "類型", "對象", "狀態", "發布時間", "操作"].map((h, i) => (
              <Box key={i} sx={{ fontSize: "13px", fontWeight: 600, color: COLORS.grayDark }}>
                {h}
              </Box>
            ))}
          </Box>
          {list.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: COLORS.grayDark, fontSize: 14 }}>目前尚無通知</Box>
          ) : (
            list.map((n) => (
              <Box
                key={n.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "26% 12% 18% 12% 16% 16%",
                  px: 3,
                  py: 2,
                  borderBottom: "1px solid #f5f5f5",
                  alignItems: "center",
                  "&:hover": { backgroundColor: COLORS.grayLightest },
                }}
              >
                <Box sx={{ fontSize: 13, color: COLORS.black, pr: 1 }}>
                  {(n.title && (n.title["zh-TW"] || Object.values(n.title)[0])) || "(無標題)"}
                </Box>
                <Box>
                  <Chip
                    label={n.type}
                    size="small"
                    sx={{ bgcolor: TYPE_COLOR[n.type] || COLORS.grayMedian, color: COLORS.white, fontSize: 11, height: 22 }}
                  />
                </Box>
                <Box sx={{ fontSize: 12, color: COLORS.grayDark }}>{audienceLabel(n)}</Box>
                <Box>
                  <Switch
                    checked={n.isActive !== false}
                    onChange={() => toggleActive(n)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: COLORS.main },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#ff8a3d !important",
                      },
                    }}
                  />
                </Box>
                <Box sx={{ fontSize: 12, color: COLORS.grayDark }}>
                  {n.publishedAt ? fmtDate(n.publishedAt) : "-"}
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => openEdit(n)} sx={{ color: COLORS.grayDark }}>
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(n.id)} sx={{ color: COLORS.error }}>
                    <Icon fontSize="small">delete</Icon>
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Card>
      </MDBox>

      {/* 新增/編輯 Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
          {form.id ? "編輯通知" : "新增通知"}
        </DialogTitle>
        <DialogContent dividers>
          {/* 2026-05-25 四語全攤開，一次填完（繁中必填，其他選填留空 fallback 繁中）*/}
          <MDTypography sx={{ fontSize: 12, color: COLORS.grayDark, mb: 1.5 }}>
            繁中為必填；其他語系留空時，該語系使用者會看到繁中內容。
          </MDTypography>
          {LOCALES.map((l, idx) => {
            const isPrimary = l.code === "zh-TW";
            return (
              <Box
                key={l.code}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #eef0f3",
                  background: isPrimary ? "#fff8f2" : COLORS.grayLightest,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1.2}>
                  <Box sx={{ width: 3, height: 14, background: COLORS.main, borderRadius: 1 }} />
                  <MDTypography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.black }}>
                    {l.label}
                    {isPrimary && <span style={{ color: COLORS.error, marginLeft: 4 }}>*</span>}
                  </MDTypography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="標題"
                  value={form.title[l.code]}
                  onChange={(e) => setTitleLang(l.code, e.target.value)}
                  sx={{ mb: 1.2, background: COLORS.white }}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="內文"
                  value={form.content[l.code]}
                  onChange={(e) => setContentLang(l.code, e.target.value)}
                  sx={{ background: COLORS.white }}
                />
              </Box>
            );
          })}

          <Divider sx={{ my: 2 }} />

          {/* type + 到期日（2026-05-25 補 size="small" + InputLabelProps shrink，label 不擠值）*/}
          <Box display="flex" gap={2} mb={2}>
            <TextField
              select
              size="small"
              label="類型"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            >
              {TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              size="small"
              label="到期日（可空=永不過期）"
              InputLabelProps={{ shrink: true }}
              value={form.expiresAt}
              onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* 分眾 */}
          <TextField
            select
            fullWidth
            size="small"
            label="發送對象"
            value={form.audienceType}
            onChange={(e) => setForm((f) => ({ ...f, audienceType: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          >
            <MenuItem value="ALL">全站使用者</MenuItem>
            <MenuItem value="PLAN">依方案分眾</MenuItem>
            <MenuItem value="USER">指定個別使用者</MenuItem>
          </TextField>

          {form.audienceType === "PLAN" && (
            <Box mb={2}>
              {PLAN_CODES.map((p) => (
                <FormControlLabel
                  key={p}
                  control={
                    <Checkbox
                      checked={form.targetPlans.includes(p)}
                      onChange={() => togglePlan(p)}
                      sx={{ "&.Mui-checked": { color: COLORS.main } }}
                    />
                  }
                  label={p}
                />
              ))}
            </Box>
          )}

          {form.audienceType === "USER" && (
            <Box mb={2}>
              <TextField
                fullWidth
                label="搜尋使用者 Email（輸入 2 字以上）"
                value={userKeyword}
                onChange={(e) => searchUsers(e.target.value)}
              />
              {userResults.length > 0 && (
                <Box sx={{ border: "1px solid #eef0f3", borderRadius: 2, mt: 1, maxHeight: 160, overflowY: "auto" }}>
                  {userResults.map((u) => (
                    <Box
                      key={u.id}
                      onClick={() => addUser(u)}
                      sx={{ px: 2, py: 1, cursor: "pointer", fontSize: 13, "&:hover": { bgcolor: COLORS.grayLightest } }}
                    >
                      {u.email} <span style={{ color: COLORS.grayMedian }}>({u.planCode})</span>
                    </Box>
                  ))}
                </Box>
              )}
              <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                {pickedUsers.map((u) => (
                  <Chip key={u.id} label={u.email} onDelete={() => removeUser(u.id)} size="small" />
                ))}
              </Box>
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: COLORS.main },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#ff8a3d !important",
                  },
                }}
              />
            }
            label="立即啟用"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={() => setDialogOpen(false)} variant="outlined" color="secondary" size="small">
            取消
          </MDButton>
          <MDButton
            onClick={handleSave}
            size="small"
            sx={{
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              fontWeight: 600,
            }}
          >
            儲存
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
